/**
 * 玩家间转账的纯派生。零 React、零 DOM、零 store —— 全部可在 node 环境单测。
 *
 * ★★ 方向只记一句话：**fromSeat 向 toSeat 买 amount 筹码**。★★
 *   fromSeat = 买家 = 申请人 = 收到筹码的人
 *   toSeat   = 卖家 = 批准人 = 付出筹码的人
 *   筹码流向与字段名相反。界面上一律读成「A 向 B 买」，永远不会搞反。
 *
 * 三条不能动的规则（与服务端同一口径）：
 *  1. 受起始筹码线约束 —— 买家到账后不能超过起始线，否则起始线规则被转账整个绕过；
 *  2. 流水**全员可见** —— 这是防 chip dumping 的唯一手段，不做任何按 viewer 的裁剪；
 *  3. 只在手牌间隙生效 —— 手牌进行中批准的排队到手牌边界（红线 #41）。
 *
 * ★ 红线 #38：本文件算出的 `max` 只是**界面上的上限**，用来在 UI 上就把不可能的数额挡住。
 *   真正转走多少由服务端在生效那一刻用 `min(申请额, 起始线 − 买家筹码, 卖家筹码)` 重算。
 *   卖家是 AI 时再按它肯卖的上限封顶（`botSaleCap`，规矩与服务端同一份）。
 */
import { BOT_SALE, DEFAULT_BOT_PERSONA, ROOM_LIFECYCLE, botSaleMax, botSellerStanding } from '@poker/shared'
import type {
  Chips,
  Millis,
  PlayerView,
  PublicSeat,
  SeatIndex,
  TransferRecord,
  TransferRequestView,
} from '@poker/shared'

/** 求购申请的有效期，与买分复用同一条（5 分钟无人处理自动作废）。 */
export const TRANSFER_TTL_MS = ROOM_LIFECYCLE.TOPUP_REQUEST_TTL_MS

// ============================================================================
// 快照读取
// ============================================================================

/** 在途申请。空时服务端**不下发**这个字段，所以一律要兜 undefined。 */
export function transfersOf(view: PlayerView | null): readonly TransferRequestView[] {
  return view?.transfers ?? []
}

/** 已生效的流水（全员可见）。空时服务端不下发。 */
export function transferLogOf(view: PlayerView | null): readonly TransferRecord[] {
  return view?.transferLog ?? []
}

function seatOf(view: PlayerView | null, seat: SeatIndex): PublicSeat | null {
  return view?.seats.find((s) => s.index === seat) ?? null
}

/** 座位上的人叫什么。没昵称的退回「3 号位」——总比一行空白强。 */
export function seatName(view: PlayerView | null, seat: SeatIndex): string {
  const nickname = seatOf(view, seat)?.occupant?.nickname ?? ''
  return nickname === '' ? `${seat + 1} 号位` : nickname
}

/** 流水里的昵称优先用记录自带的那份（人可能已经离座，座位上查不到了）。 */
function recordName(view: PlayerView | null, seat: SeatIndex, snapshot: string): string {
  return snapshot === '' ? seatName(view, seat) : snapshot
}

// ============================================================================
// 我能向谁买、能买多少
// ============================================================================

/**
 * 界面上的可买上限 = `min(对方筹码, 起始线 − 我的筹码)`。
 *
 * 两个上限缺一不可，而且**必须在界面上就挡住**：让人拖到一个必被服务端钳掉的数额，
 * 再回一条错误，等于让他白填一次。
 */
export function transferAllowance(view: PlayerView | null, toSeat: SeatIndex): Chips {
  if (view === null) return 0
  const mySeat = view.viewer.ownedSeat
  if (mySeat === null || mySeat === toSeat) return 0
  const me = seatOf(view, mySeat)
  const other = seatOf(view, toSeat)
  if (me === null || other === null || other.occupant === null) return 0
  const headroom = view.room.config.startingStack - me.stack
  return Math.max(0, Math.min(headroom, other.stack))
}

/**
 * 向 AI 求购时，它按自己的硬规矩最多肯卖多少（规矩与服务端同一份：shared 的 botSale.ts）。
 *
 * ★ 以前界面只按「对方筹码 / 起始线」算上限，主按钮要的就是这个最大值 —— 可 AI 卖完要留底线、
 *   一次不卖超过四分之三，于是向 AI 买几乎必被拒，只收到一句「对方没答应」，不知道为什么。
 * 返回 null = 卖家不是 AI（真人自己决定，界面不替他设限）。
 * 只用全员可见的数据（公开流水 + 在途申请），只可能把上限算松、不会算紧（见 botSellerStanding）。
 */
export interface BotSaleCap {
  readonly max: Chips
  /** max 为 0 时的原因；否则 null */
  readonly reason: string | null
}

export function botSaleCap(view: PlayerView | null, toSeat: SeatIndex): BotSaleCap | null {
  if (view === null) return null
  const mySeat = view.viewer.ownedSeat
  if (mySeat === null) return null
  const me = seatOf(view, mySeat)
  const seller = seatOf(view, toSeat)
  const bot = seller?.occupant ?? null
  if (seller === null || bot === null || !bot.isBot || me?.occupant == null) return null

  const standing = botSellerStanding(
    { accountId: bot.accountId, stack: seller.stack },
    me.occupant.accountId,
    transferLogOf(view),
    transfersOf(view),
  )
  const max = botSaleMax(bot.botPersona ?? DEFAULT_BOT_PERSONA, standing, view.room.config)
  if (max > 0) return { max, reason: null }
  // 三条规矩对应三句话：换个 AI 买、等它赢一点、这局别找它了
  const reason =
    standing.soldToBuyer >= BOT_SALE.MAX_SALES_PER_BUYER
      ? 'TA 已经卖给你两次了'
      : standing.soldTotal >= view.room.config.startingStack
        ? 'TA 这局卖得够多了'
        : 'TA 筹码不多，不卖'
  return { max: 0, reason }
}

/** 我向某个座位发出去的那一笔在途申请（服务端保证同一对只有一笔）。 */
export function transferToSeat(view: PlayerView | null, toSeat: SeatIndex): TransferRequestView | null {
  const mySeat = view?.viewer.ownedSeat ?? null
  if (mySeat === null) return null
  return transfersOf(view).find((r) => r.fromSeat === mySeat && r.toSeat === toSeat) ?? null
}

/** 求购入口的状态。`blocked` 非 null 时入口显示成一句话，而不是一颗点了没用的按钮。 */
export interface TransferTargetModel {
  readonly seat: SeatIndex
  readonly nickname: string
  /** 对方现在有多少筹码 */
  readonly stack: Chips
  /** 我现在有多少 */
  readonly myStack: Chips
  readonly startingStack: Chips
  /** 界面上的可买上限；0 表示买不了 */
  readonly max: Chips
  /** 买不了的原因；null = 能买 */
  readonly blocked: string | null
  /** 我已经向他发过的那一笔（pending / approved） */
  readonly pending: TransferRequestView | null
}

/** 「这个座位上是谁」。与「能不能向他买筹码」是两件事，必须分开。 */
export interface SeatProfileModel {
  readonly seat: SeatIndex
  readonly nickname: string
  readonly stack: Chips
  readonly avatarSeed: string
  readonly accountId: string
  readonly isBot: boolean
  readonly isHost: boolean
}

/**
 * 座位上那个人的名片。
 *
 * ★ 与 `transferTarget` 分开的理由：那个函数在「我没坐下」时恒为 null（观战者
 *   本来就买不了筹码）。可「看看这人是谁、头像长什么样」跟我坐没坐下毫无关系 ——
 *   两件事挂同一个判据的话，观战的人点谁都没反应。
 * ★ 自己那一格仍然不给：自己的头像在大厅的头像面板里，在自己脸上再开一个入口只是多一次误触。
 */
export function seatProfile(view: PlayerView | null, seat: SeatIndex | null): SeatProfileModel | null {
  if (view === null || seat === null) return null
  if (view.viewer.ownedSeat === seat) return null
  const row = seatOf(view, seat)
  const who = row?.occupant
  if (row === null || who == null) return null
  return {
    seat,
    nickname: who.nickname === '' ? `${seat + 1} 号位` : who.nickname,
    stack: row.stack,
    avatarSeed: who.avatarSeed,
    accountId: who.accountId,
    isBot: who.isBot,
    isHost: who.isHost,
  }
}

/**
 * 我能不能向这个座位买筹码。
 *
 * 拒绝的理由分开说，不并成一句「现在不能买」：三种情况要改的东西完全不同
 * （等自己输一点 / 换个人买 / 这局已经结束了）。
 */
export function transferTarget(view: PlayerView | null, seat: SeatIndex | null): TransferTargetModel | null {
  if (view === null || seat === null) return null
  const mySeat = view.viewer.ownedSeat
  if (mySeat === null || mySeat === seat) return null
  const me = seatOf(view, mySeat)
  const other = seatOf(view, seat)
  if (me === null || other === null || other.occupant === null) return null

  const startingStack = view.room.config.startingStack
  // 卖家是 AI 时再按它的硬规矩封顶：档位和主按钮都不会要一个它必拒的数
  const botCap = botSaleCap(view, seat)
  const max = Math.min(transferAllowance(view, seat), botCap?.max ?? Infinity)
  const pending = transferToSeat(view, seat)
  const settled = view.settlement !== undefined || view.room.phase === 'closed'
  const blocked =
    settled
      ? '牌局已经结束了'
      : me.stack >= startingStack
        ? '你的筹码已达起始线'
        : other.stack <= 0
          ? '对方没有筹码可卖'
          : botCap !== null && botCap.max <= 0
            ? botCap.reason
            : max <= 0
              ? '现在买不了'
              : null

  return {
    seat,
    nickname: seatName(view, seat),
    stack: other.stack,
    myStack: me.stack,
    startingStack,
    max,
    blocked,
    pending,
  }
}

/**
 * 快捷档位。
 *
 * 不给滑块的理由：手机上一条 0..3000 的滑块，拖到「正好 1500」要试好几次，
 * 而真实场景里大家要的就是「半个买入」「补满」这么几个数。
 * 一律按大盲取整，避免出现 1337 这种没人想要的数。
 */
export function quickAmounts(max: Chips, bigBlind: Chips): readonly Chips[] {
  if (max <= 0) return []
  const step = bigBlind > 0 ? bigBlind : 1
  const round = (n: number): Chips => Math.max(step, Math.min(max, Math.round(n / step) * step))
  const out: Chips[] = []
  for (const n of [round(max / 4), round(max / 2), max]) {
    if (n > 0 && !out.includes(n)) out.push(n)
  }
  return out
}

// ============================================================================
// 待我批准 / 我发出去的
// ============================================================================

/** 别人向**我**求购、等我批准的。★ 批准人是卖家本人，不是房主。 */
export function incomingTransfers(view: PlayerView | null): readonly TransferRequestView[] {
  const mySeat = view?.viewer.ownedSeat ?? null
  if (mySeat === null) return []
  return transfersOf(view)
    .filter((r) => r.toSeat === mySeat)
    .sort((a, b) => a.requestedAt - b.requestedAt)
}

/** 我发出去、还没生效的。 */
export function outgoingTransfers(view: PlayerView | null): readonly TransferRequestView[] {
  const mySeat = view?.viewer.ownedSeat ?? null
  if (mySeat === null) return []
  return transfersOf(view)
    .filter((r) => r.fromSeat === mySeat)
    .sort((a, b) => a.requestedAt - b.requestedAt)
}

/**
 * 角标上的数字。**只数 pending**：已批准的那笔我已经处理过了，
 * 继续计进角标等于催我再点一次一件做完的事。
 */
export function pendingIncomingCount(view: PlayerView | null): number {
  return incomingTransfers(view).filter((r) => r.status === 'pending').length
}

/** 要不要在顶栏挂这个角标。一笔都没有就完全不出现。 */
export function showTransferQueue(view: PlayerView | null): boolean {
  return incomingTransfers(view).length > 0
}

// ============================================================================
// 流水（全员可见）
// ============================================================================

export interface TransferLogLine {
  readonly key: string
  /** 买家（收到筹码的人） */
  readonly to: string
  /** 卖家（付出筹码的人） */
  readonly from: string
  readonly amount: Chips
  readonly at: Millis
  /** 「3 分钟前」 */
  readonly ago: string
}

/**
 * 流水行。最近的排最前 —— 这张表是用来「回头看一眼刚才谁给了谁」的，
 * 不是一份从头读到尾的账本。
 *
 * ★ 不按 viewer 裁剪：这是刻意公开的信息（规则 2）。
 */
export function transferLogLines(view: PlayerView | null, now: Millis): readonly TransferLogLine[] {
  return [...transferLogOf(view)]
    .sort((a, b) => b.at - a.at)
    .map((r) => ({
      key: r.requestId,
      to: recordName(view, r.fromSeat, r.fromNickname),
      from: recordName(view, r.toSeat, r.toNickname),
      amount: r.amount,
      at: r.at,
      ago: agoText(r.at, now),
    }))
}

/**
 * 「多久以前」。只给到分钟：一笔转账发生在 3 分 12 秒前还是 3 分 40 秒前，
 * 没有任何人关心，而每秒跳一次的秒数会把一张静态列表变成闪烁的招牌。
 */
export function agoText(at: Millis, now: Millis): string {
  const ms = Math.max(0, now - at)
  const min = Math.floor(ms / 60_000)
  if (min < 1) return '刚刚'
  if (min < 60) return `${min} 分钟前`
  const h = Math.floor(min / 60)
  return h < 24 ? `${h} 小时前` : `${Math.floor(h / 24)} 天前`
}
